/**
 * Indian Financial Year helpers.
 * Indian FY runs April 1 → March 31.
 * "FY 2024-25" means Apr 2024 – Mar 2025; its fyStartYear is 2024.
 *
 * Quarters (within a given FY):
 *   Q1 = Apr–Jun  (of fyStartYear)
 *   Q2 = Jul–Sep  (of fyStartYear)
 *   Q3 = Oct–Dec  (of fyStartYear)
 *   Q4 = Jan–Mar  (of fyStartYear + 1)
 */

/** Returns the fyStartYear of the current (or most recent completed) Indian FY. */
export const getMaxIndianFYStartYear = (): number => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-based
  return month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

/**
 * Returns YYYY-MM strings for a specific FY quarter.
 * @param fyYear  fyStartYear as string, e.g. "2024"
 * @param quarter "Q1" | "Q2" | "Q3" | "Q4" | "Full"
 */
export const getYearMonthsForFYQuarter = (
  fyYear: string,
  quarter: string
): string[] => {
  const y = parseInt(fyYear, 10);
  if (isNaN(y)) return [];

  switch (quarter) {
    case 'Q1':
      return [`${y}-04`, `${y}-05`, `${y}-06`];
    case 'Q2':
      return [`${y}-07`, `${y}-08`, `${y}-09`];
    case 'Q3':
      return [`${y}-10`, `${y}-11`, `${y}-12`];
    case 'Q4':
      return [`${y + 1}-01`, `${y + 1}-02`, `${y + 1}-03`];
    default:
      return getYearMonthsForFullFY(fyYear);
  }
};

/**
 * Returns all 12 YYYY-MM strings for a full Indian FY.
 * e.g. fyYear="2024" → ["2024-04", ..., "2025-03"]
 */
export const getYearMonthsForFullFY = (fyYear: string): string[] => {
  const y = parseInt(fyYear, 10);
  if (isNaN(y)) return [];
  return [
    `${y}-04`, `${y}-05`, `${y}-06`,
    `${y}-07`, `${y}-08`, `${y}-09`,
    `${y}-10`, `${y}-11`, `${y}-12`,
    `${y + 1}-01`, `${y + 1}-02`, `${y + 1}-03`,
  ];
};

/**
 * Returns the union of YYYY-MM keys for multiple FY start years and an optional quarter.
 * @param fyStartYears  array of fyStartYear strings
 * @param quarter       "Q1"|"Q2"|"Q3"|"Q4"|"Full"|"" (empty string = Full)
 */
export const mergeYearMonthsForMultipleFYStartYears = (
  fyStartYears: string[],
  quarter: string
): string[] => {
  const set = new Set<string>();
  const q = quarter || 'Full';
  for (const fyYear of fyStartYears) {
    const months =
      q === 'Full'
        ? getYearMonthsForFullFY(fyYear)
        : getYearMonthsForFYQuarter(fyYear, q);
    for (const m of months) set.add(m);
  }
  return Array.from(set).sort();
};

/**
 * Deep-equality check for two arrays of fyStartYear strings
 * (order-insensitive).
 */
export const sameFyStartYearSelection = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

/** Formats a fyStartYear number as a human-readable FY label. */
export const fyLabel = (startYear: number): string =>
  `FY ${startYear}-${String(startYear + 1).slice(2)}`;
